import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Truck, Sparkles, Mail } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import EnquiryForm from "../components/EnquiryForm";
import { api, formatINR } from "../lib/api";

export default function Chocolates() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const active = params.get("category") || "";

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.filter((c) => c.parent_slug === "chocolates" || c.slug === "chocolates"))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: { product_type: "chocolate", ...(active ? { category: active } : {}) } })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [active]);

  const subs = categories.filter((c) => c.parent_slug === "chocolates");

  return (
    <div className="App">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10 lg:py-20" data-testid="chocolates-page">
        <p className="label-tiny mb-3">Customised Chocolates</p>
        <h1 className="font-serif text-4xl text-[#2a2928] sm:text-5xl lg:text-6xl" data-testid="chocolates-heading">
          Sweet, wrapped in <span className="italic text-[#d08c8c]">your story.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-[#7a7571]">
          We don&apos;t manufacture chocolates — we wrap premium chocolates in
          your custom designs, names or occasion artwork. Pick a style below and
          send us an enquiry. Our team will contact you within 24 hours with a
          quote and design options. Minimum order: 100 chocolates.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-[#7a7571]">
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 border border-[#e8e2da]">
            <Truck className="h-3.5 w-3.5 text-[#d08c8c]" /> Shipped all-India
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 border border-[#e8e2da]">
            <Sparkles className="h-3.5 w-3.5 text-[#d08c8c]" /> Fully customisable wrappers
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 border border-[#e8e2da]">
            Min order 100 pcs
          </span>
          <button
            onClick={() => setEnquiryOpen(true)}
            data-testid="chocolates-enquire-cta"
            className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#d08c8c] px-5 py-2 text-sm font-medium text-white hover:bg-[#b87a7a]"
          >
            <Mail className="h-4 w-4" /> Send an enquiry
          </button>
        </div>

        <div className="mt-10 flex flex-wrap gap-2 border-t border-[#e8e2da] pt-8" data-testid="chocolates-filters">
          <button
            onClick={() => { params.delete("category"); setParams(params); }}
            data-testid="choc-filter-all"
            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
              !active ? "border-[#d08c8c] bg-[#d08c8c] text-white" : "border-[#e8e2da] bg-white text-[#2a2928] hover:border-[#d08c8c]"
            }`}
          >
            All
          </button>
          {subs.map((c) => (
            <button
              key={c.slug}
              data-testid={`choc-filter-${c.slug}`}
              onClick={() => { params.set("category", c.slug); setParams(params); }}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                active === c.slug ? "border-[#d08c8c] bg-[#d08c8c] text-white" : "border-[#e8e2da] bg-white text-[#2a2928] hover:border-[#d08c8c]"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="chocolates-grid">
          {loading && [...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[4/5] rounded-3xl bg-[#f0e9e1]" />
              <div className="mt-4 h-4 w-2/3 rounded bg-[#f0e9e1]" />
            </div>
          ))}
          {!loading && products.length === 0 && (
            <div className="col-span-full rounded-3xl border border-dashed border-[#e8e2da] py-20 text-center text-[#7a7571]" data-testid="chocolates-empty">
              No chocolates in this category yet.
            </div>
          )}
          {!loading && products.map((p) => (
            <Link
              to={`/product/${p.id}`}
              key={p.id}
              data-testid={`choc-product-${p.id}`}
              className="group card-lift"
            >
              <div className="overflow-hidden rounded-3xl border border-[#e8e2da] bg-white">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={p.image_base64 || p.image_url}
                    alt={p.name}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </div>
              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-[#7a7571]">
                    {categories.find((c) => c.slug === p.category)?.name || p.category}
                  </p>
                  <h3 className="mt-1 font-serif text-xl text-[#2a2928]">{p.name}</h3>
                  {p.min_order_quantity > 1 && (
                    <p className="mt-1 text-xs text-[#7a7571]">Min order: {p.min_order_quantity} pcs · enquiry</p>
                  )}
                </div>
                <div className="whitespace-nowrap font-serif text-lg text-[#2a2928]">
                  {formatINR(p.price)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <EnquiryForm open={enquiryOpen} onOpenChange={setEnquiryOpen} product={null} />
      <Footer />
    </div>
  );
}
